import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, ShoppingBag, ShoppingCart,
  User, MessageSquare, BarChart3, Truck,
  History, DollarSign, ChevronLeft,
  ChevronRight, Package, Tag, Scale, Heart, UserCheck
} from 'lucide-react';
import { UserRole } from '@/types/user.types';
import { cn } from '../../lib/utils';
import { Logo } from '@/components/ui/Logo';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  userRole?: UserRole;
  isMobile?: boolean;
  mobileDrawerOpen?: boolean;
  onMobileDrawerClose?: () => void;
}

const navigationByRole = {
  [UserRole.CUSTOMER]: [
    { path: '/home', icon: LayoutDashboard, label: 'Home' },
    { path: '/cart', icon: ShoppingCart, label: 'Cart' },
    { path: '/orders', icon: Package, label: 'My Orders' },
    { path: '/favourites', icon: Heart, label: 'Favourites' },
    { path: '/profile', icon: User, label: 'Profile' },
  ],
  [UserRole.FARMER]: [
    { path: '/farmer', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/farmer/products', icon: ShoppingBag, label: 'My Products' },
    { path: '/farmer/orders', icon: Package, label: 'Incoming Orders' },
    { path: '/farmer/dispatch', icon: Truck, label: 'Dispatch' },
    { path: '/orders', icon: ShoppingCart, label: 'My Purchases' },
    { path: '/farmer/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/farmer/messages', icon: MessageSquare, label: 'Messages', disabled: true, badge: 'Soon' },
    { path: '/farmer/profile', icon: User, label: 'Profile' },
  ],
  [UserRole.DELIVERY_AGENT]: [
    { path: '/delivery', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/delivery/requests', icon: MessageSquare, label: 'Requests' },
    { path: '/delivery/active', icon: Truck, label: 'Active Deliveries' },
    { path: '/delivery/history', icon: History, label: 'History' },
    { path: '/delivery/earnings', icon: DollarSign, label: 'Earnings' },
    { path: '/delivery/profile', icon: User, label: 'Profile' },
  ],
  [UserRole.SUPPLIER]: [
    { path: '/supplier', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/supplier/catalog', icon: ShoppingBag, label: 'Catalog' },
    { path: '/supplier/orders', icon: Package, label: 'Incoming Orders' },
    { path: '/supplier/dispatch', icon: Truck, label: 'Dispatch' },
    { path: '/orders', icon: ShoppingCart, label: 'My Purchases' },
    { path: '/supplier/profile', icon: User, label: 'Profile' },
  ],
  [UserRole.ADMIN]: [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: User, label: 'Users' },
    { path: '/admin/farmer-approvals', icon: UserCheck, label: 'Farmer Approvals' },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: Tag, label: 'Categories' },
    { path: '/admin/units', icon: Scale, label: 'Units' },
    { path: '/admin/profile', icon: User, label: 'Profile' },
  ],
};

export const Sidebar = ({
  collapsed,
  onCollapse,
  userRole,
  isMobile,
  mobileDrawerOpen,
  onMobileDrawerClose,
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = userRole ? navigationByRole[userRole] : [];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onMobileDrawerClose) {
      onMobileDrawerClose();
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div className={cn(
        "h-20 flex items-center px-6 mb-2 transition-all duration-300",
        collapsed ? "justify-center px-2" : "justify-start"
      )}>
        <div className="flex items-center gap-3">
          <Logo size="md" showWordmark={false} />
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="font-display font-bold tracking-tight text-xl text-secondary-900 dark:text-white"
            >
              Aso<span className="text-primary-500">gro</span>
            </motion.span>
          )}
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto py-4">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          const isDisabled = (item as any).disabled;
          const badge = (item as any).badge;

          return (
            <button
              key={item.path}
              onClick={() => !isDisabled && handleNavigation(item.path)}
              disabled={isDisabled}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden",
                isDisabled
                  ? "opacity-50 cursor-not-allowed text-secondary-400 dark:text-secondary-600"
                  : isActive
                  ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 font-semibold shadow-sm"
                  : "text-secondary-500 hover:bg-secondary-50 hover:text-secondary-900 dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100",
                collapsed && "justify-center px-0"
              )}
            >
              {isActive && !isDisabled && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary-500 rounded-r-full"
                />
              )}
              <Icon
                size={22}
                className={cn(
                  "shrink-0 transition-colors",
                  isActive && !isDisabled ? "text-primary-600 dark:text-primary-400" : "text-secondary-400 group-hover:text-secondary-600 dark:text-secondary-500 dark:group-hover:text-secondary-300"
                )}
              />
              {!collapsed && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="truncate">{item.label}</span>
                  {badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-secondary-200 dark:bg-secondary-700 text-secondary-500 dark:text-secondary-400">
                      {badge}
                    </span>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-secondary-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                  {item.label} {badge ? `(${badge})` : ''}
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Collapse Toggle (Desktop) */}
      {!isMobile && (
        <div className="p-4 border-t border-secondary-100 dark:border-secondary-800">
          <button
            onClick={() => onCollapse(!collapsed)}
            className="w-full flex items-center justify-center h-10 rounded-xl bg-secondary-50 hover:bg-secondary-100 dark:bg-secondary-800 dark:hover:bg-secondary-700 text-secondary-500 transition-colors"
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>
      )}
    </div>
  );

  // Mobile Drawer
  if (isMobile) {
    return (
      <AnimatePresence>
        {mobileDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onMobileDrawerClose}
              className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] bg-white dark:bg-secondary-900 z-50 shadow-2xl"
            >
              <SidebarContent />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop Sidebar
  return (
    <motion.aside
      animate={{ width: collapsed ? 80 : 260 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-0 left-0 bottom-0 bg-white dark:bg-secondary-900 border-r border-secondary-100 dark:border-secondary-800 z-20 hidden lg:block"
    >
      <SidebarContent />
    </motion.aside>
  );
};
